/**
 * CAPTCHA state for the public auth forms; the widget is
 * `components/CaptchaWidget.tsx`. Which provider and its public site key come
 * from `GET /capabilities`.
 */
import { useCallback, useState } from "react";

import { useCapabilities } from "@/lib/capabilities";

export type CaptchaProvider = "turnstile" | "hcaptcha";

/** Exact backend detail for "this sign-in needs the CAPTCHA" (app/core/captcha.py). */
export const CAPTCHA_REQUIRED_DETAIL = "CAPTCHA required";
export const CAPTCHA_PROMPT = "Please complete the check below, then try again.";

/** The deployment's CAPTCHA, or null while it is off. */
export function useCaptchaConfig(): {
  provider: CaptchaProvider;
  siteKey: string;
  loginAfterFailures: number;
} | null {
  const { captcha } = useCapabilities();
  if (!captcha || captcha.provider === "none" || !captcha.site_key) return null;
  return {
    provider: captcha.provider,
    siteKey: captcha.site_key,
    loginAfterFailures: captcha.login_after_failures,
  };
}

/** Token state for a form: the current token, a setter for the widget and a reset. */
export function useCaptchaToken() {
  const [token, setToken] = useState<string | null>(null);
  const [resetKey, setResetKey] = useState(0);
  const reset = useCallback(() => {
    setToken(null);
    setResetKey((k) => k + 1);
  }, []);
  return { token, setToken, resetKey, reset };
}
